import R from 'ramda'
import Sjcl from '@cryptolize/sjcl'

function convertKeyFromHexToBase64 (key, prop) {
  return R.evolve({ [prop]: R.compose(Sjcl.codec.base64url.fromBits, Sjcl.codec.hex.toBits) })(key)
}

function convertKeyFromBase64ToHex (key, prop) {
  return R.evolve({ [prop]: R.compose(Sjcl.codec.hex.fromBits, Sjcl.codec.base64url.toBits) })(key)
}

/**
 * Create ECC (P-521 NIST curve) public and private keys
 *
 * @param {ElGamalPrivateKey} [privateKey] create the keys from specific private key
 * @return {{public: ElGamalPublicKey, private: PrivateKey}}
 * @throws {Error} if a parameter is invalid or generator isn't seeded
 * @public
 * @example
 *  const keys = CryptolizeCore.createAsymmetricKeys()
 *  console.log(keys.public)
 *  console.log(keys.private)
 */
export function createAsymmetricKeys (privateKey) {
  const keys = Sjcl.ecc.elGamal.generateKeys(Sjcl.ecc.curves.c521, 6, privateKey && Sjcl.bn.fromBits(Sjcl.ecc.deserialize(convertKeyFromBase64ToHex(JSON.parse(Sjcl.codec.utf8String.fromBits(Sjcl.codec.base64url.toBits(privateKey))), 'exponent')).get()))
  return {
    public: Sjcl.codec.base64url.fromBits(Sjcl.codec.utf8String.toBits(JSON.stringify(convertKeyFromHexToBase64(keys.pub.serialize(), 'point')))),
    private: Sjcl.codec.base64url.fromBits(Sjcl.codec.utf8String.toBits(JSON.stringify(convertKeyFromHexToBase64(keys.sec.serialize(), 'exponent'))))
  }
}

/**
 * Create AES encryption key from public key
 *
 * @param {ElGamalPublicKey} publicKey
 * @return {{key: Base64UrlString, tag: Base64UrlString}} encryption params
 * @throws {Error} if a parameter is invalid or generator isn't seeded
 * @private
 */
export function createSymmetricEncryptionKeyFromPublicKey (publicKey) {
  return R.evolve(
    { key: Sjcl.codec.base64url.fromBits, tag: Sjcl.codec.base64url.fromBits },
    Sjcl.ecc.deserialize(convertKeyFromBase64ToHex(JSON.parse(Sjcl.codec.utf8String.fromBits(Sjcl.codec.base64url.toBits(publicKey))), 'point')).kem()
  )
}

/**
 * Create AES encryption key from private key
 *
 * @param {ElGamalPrivateKey} privateKey
 * @param {Base64UrlString} tag the tag to generate the symmetrical key from
 * @return {Base64UrlString}
 * @throws {Error} if a parameter is invalid
 * @private
 */
export function createSymmetricEncryptionKeyFromPrivateKey (privateKey, tag) {
  return Sjcl.codec.base64url.fromBits(
    Sjcl.ecc.deserialize(
      convertKeyFromBase64ToHex(JSON.parse(Sjcl.codec.utf8String.fromBits(Sjcl.codec.base64url.toBits(privateKey))), 'exponent')
    ).unkem(
      Sjcl.codec.base64url.toBits(tag)
    )
  )
}

/**
 * Create ECC (P-521 NIST curve) public and private keys for ECDSA
 *
 * @param {ECDSAPrivateKey} [privateKey] create the keys from specific private key
 * @return {{public: PublicKey, private: PrivateKey}}
 * @throws {Error} if a parameter is invalid or generator isn't seeded
 * @public
 * @example
 *  const keys = CryptolizeCore.createAsymmetricKeysECDSA()
 *  console.log(keys.public)
 *  console.log(keys.private)
 */
export function createAsymmetricKeysECDSA (privateKey) {
  const keys = Sjcl.ecc.ecdsa.generateKeys(Sjcl.ecc.curves.c521, 6, privateKey && Sjcl.bn.fromBits(Sjcl.ecc.deserialize(convertKeyFromBase64ToHex(JSON.parse(Sjcl.codec.utf8String.fromBits(Sjcl.codec.base64url.toBits(privateKey))), 'exponent')).get()))
  return {
    public: Sjcl.codec.base64url.fromBits(Sjcl.codec.utf8String.toBits(JSON.stringify(convertKeyFromHexToBase64(keys.pub.serialize(), 'point')))),
    private: Sjcl.codec.base64url.fromBits(Sjcl.codec.utf8String.toBits(JSON.stringify(convertKeyFromHexToBase64(keys.sec.serialize(), 'exponent'))))
  }
}

/**
 * Sign text with ECDSA private key
 *
 * @param {ECDSAPrivateKey} privateKey the key to sign with
 * @param {String} text the text to sign
 * @return {Base64UrlString}
 * @public
 * @example
 *  const keys = CryptolizeCore.createAsymmetricKeysECDSA()
 *  const signature = CryptolizeCore.signWithECDSAPrivateKey(keys.private, 'text to sign')
 */
export function signWithECDSAPrivateKey (privateKey, text) {
  return Sjcl.codec.base64url.fromBits(
    Sjcl.ecc.deserialize(
      convertKeyFromBase64ToHex(JSON.parse(Sjcl.codec.utf8String.fromBits(Sjcl.codec.base64url.toBits(privateKey))), 'exponent')
    ).sign(
      Sjcl.hash.sha512.hash(text)
    )
  )
}

/**
 * Verify text with ECDSA public key
 *
 * @param {ECDSAPublicKey} publicKey the key to verify with
 * @param {String} text the text to verify
 * @param {Base64UrlString} signature the signature to verify with
 * @return {Boolean}
 * @public
 * @example
 *  const keys = CryptolizeCore.createAsymmetricKeysECDSA()
 *  const signature = CryptolizeCore.signWithECDSAPrivateKey(keys.private, 'text to sign')
 *  const isOk = CryptolizeCore.verifyWithECDSAPublicKey(keys.public, 'text to sign', signature)
 */
export function verifyWithECDSAPublicKey (publicKey, text, signature) {
  try {
    return Sjcl.ecc.deserialize(
      convertKeyFromBase64ToHex(JSON.parse(Sjcl.codec.utf8String.fromBits(Sjcl.codec.base64url.toBits(publicKey))), 'point')
    ).verify(
      Sjcl.hash.sha512.hash(text), Sjcl.codec.base64url.toBits(signature)
    )
  } catch (e) {
    return false
  }
}
